import React, { useEffect, useCallback, useState, useRef } from 'react';
import AuthenticatedImage from './AuthenticatedImage';
import { SlideImage } from '../types';

interface ImageViewModalProps {
  shopId: string;
  slideId: string;
  imageId: string;
  imageDescription?: string;
  onClose: () => void;
  allImages?: SlideImage[];
  onNavigate?: (imageId: string) => void;
}

const ImageViewModal: React.FC<ImageViewModalProps> = ({
  shopId,
  slideId,
  imageId,
  imageDescription,
  onClose,
  allImages,
  onNavigate
}) => {
  // Internal navigation state for instant switching
  const initialIndex = allImages ? allImages.findIndex(img => img.id === imageId) : -1;
  const [currentIndex, setCurrentIndex] = useState(initialIndex >= 0 ? initialIndex : 0);
  const hasMultipleImages = allImages && allImages.length > 1;

  // Current image info
  const currentImage = allImages?.[currentIndex];
  const currentDescription = currentImage?.description || imageDescription;

  // Touch/swipe handling
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Desktop drag handling
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const swipedRef = useRef(false);

  const minSwipeDistance = 50;

  const handlePrevious = useCallback(() => {
    if (!allImages) return;
    const prevIndex = currentIndex === 0 ? allImages.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    onNavigate?.(allImages[prevIndex].id);
  }, [allImages, onNavigate, currentIndex]);

  const handleNext = useCallback(() => {
    if (!allImages) return;
    const nextIndex = currentIndex === allImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(nextIndex);
    onNavigate?.(allImages[nextIndex].id);
  }, [allImages, onNavigate, currentIndex]);

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current || !hasMultipleImages) return;

    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrevious();
    }

    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  // Desktop drag handlers
  const onDragStart = (e: React.MouseEvent) => {
    if (!hasMultipleImages) return;
    dragStartRef.current = e.clientX;
    isDraggingRef.current = false;
    swipedRef.current = false;
  };

  const onDragMove = (e: React.MouseEvent) => {
    if (dragStartRef.current === null) return;
    const diff = Math.abs(e.clientX - dragStartRef.current);
    if (diff > 5) {
      isDraggingRef.current = true;
    }
  };

  const onDragEnd = (e: React.MouseEvent) => {
    if (dragStartRef.current === null) return;
    const distance = dragStartRef.current - e.clientX;

    if (Math.abs(distance) > minSwipeDistance) {
      swipedRef.current = true;
      if (distance > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }

    dragStartRef.current = null;
    isDraggingRef.current = false;
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {
        handlePrevious();
      } else if (e.key === 'ArrowRight' && hasMultipleImages) {
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [onClose, hasMultipleImages, handlePrevious, handleNext]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
        cursor: 'pointer'
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'default'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '-40px',
            right: 0,
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '32px',
            cursor: 'pointer',
            padding: '4px 12px',
            lineHeight: 1,
            zIndex: 2001
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          aria-label="Close image view"
        >
          ×
        </button>

        {/* Left Arrow - Previous Image */}
        {hasMultipleImages && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            style={{
              position: 'absolute',
              left: '-60px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '24px',
              color: 'var(--text-primary)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease',
              zIndex: 2001
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            }}
            aria-label="Previous image"
          >
            ‹
          </button>
        )}

        {/* Right Arrow - Next Image */}
        {hasMultipleImages && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            style={{
              position: 'absolute',
              right: '-60px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '24px',
              color: 'var(--text-primary)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease',
              zIndex: 2001
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            }}
            aria-label="Next image"
          >
            ›
          </button>
        )}

        {/* Image Container - All images rendered, only current visible */}
        <div
          ref={imageContainerRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={() => { dragStartRef.current = null; isDraggingRef.current = false; }}
          style={{
            position: 'relative',
            maxWidth: '100%',
            maxHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: hasMultipleImages ? 'pan-y' : 'auto',
            userSelect: 'none',
            cursor: hasMultipleImages ? 'grab' : 'default'
          }}
        >
          {allImages && allImages.length > 0 ? (
            allImages.map((image, index) => (
              <AuthenticatedImage
                key={image.id}
                shopId={shopId}
                slideId={slideId}
                imageId={image.id}
                alt={image.description || 'Slide image'}
                size="thumb_800"
                loading="eager"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none',
                  ...(index !== currentIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                }}
              />
            ))
          ) : (
            <AuthenticatedImage
              shopId={shopId}
              slideId={slideId}
              imageId={imageId}
              alt={imageDescription || 'Slide image'}
              size="thumb_800"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none'
              }}
            />
          )}
        </div>

        {/* Dot Indicators */}
        {hasMultipleImages && (
          <div
            style={{
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)'
            }}
          >
            {allImages!.slice(0, 7).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: index === currentIndex ? 'var(--bg-card)'
                    : (allImages!.length > 7 && currentIndex >= 7 && index === 6) ? 'white'
                    : 'rgba(255, 255, 255, 0.4)',
                  transition: 'background-color 0.2s ease'
                }}
              />
            ))}
          </div>
        )}

        {/* Image Description */}
        {currentDescription && (
          <div
            style={{
              marginTop: '12px',
              color: 'white',
              fontSize: '14px',
              textAlign: 'center',
              padding: '8px 16px',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '4px',
              maxWidth: '800px'
            }}
          >
            {currentDescription}
          </div>
        )}

        {/* Instructions */}
        <div
          style={{
            marginTop: '12px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '12px',
            textAlign: 'center'
          }}
        >
          {hasMultipleImages
            ? 'Swipe or use arrow keys to navigate • Press ESC to close'
            : 'Click anywhere or press ESC to close'
          }
        </div>
      </div>
    </div>
  );
};

export default ImageViewModal;
